/**
 * Centralized Cart Handler
 * Serial queue for all cart API requests to prevent race conditions.
 * Every add/change/update goes through this handler so responses
 * are always processed in order.
 */
window.CartHandler = (function () {
  var queue = [];
  var processing = false;
  var requestId = 0;

  function processNext() {
    if (processing || queue.length === 0) return;
    processing = true;
    var task = queue.shift();
    task.execute().finally(function () {
      processing = false;
      processNext();
    });
  }

  function enqueue(task) {
    return new Promise(function (resolve, reject) {
      queue.push({
        execute: function () {
          return task().then(resolve, reject);
        },
      });
      processNext();
    });
  }

  function getSections() {
    var drawer = document.querySelector('cart-drawer');
    var cartItems = document.getElementById('main-cart-items');
    var sections = ['cart-icon-bubble', 'cart-live-region-text'];

    if (drawer) {
      drawer.getSectionsToRender().forEach(function (s) {
        if (sections.indexOf(s.id) === -1) sections.push(s.id);
      });
    }
    if (cartItems) {
      var cartItemsSection = cartItems.dataset.id;
      if (sections.indexOf(cartItemsSection) === -1) sections.push(cartItemsSection);
      var footer = document.getElementById('main-cart-footer');
      if (footer && footer.dataset.id && sections.indexOf(footer.dataset.id) === -1) {
        sections.push(footer.dataset.id);
      }
    }
    return sections;
  }

  function renderSections(data) {
    if (!data.sections) return;

    /* Cart drawer — update items + checkout total without touching recommendations */
    var drawer = document.querySelector('cart-drawer');
    if (drawer && data.sections['cart-drawer']) {
      var drawerParsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');

      // Update cart items
      var drawerItemsSource = drawerParsed.querySelector('cart-drawer-items');
      var drawerItemsTarget = document.querySelector('cart-drawer-items');
      if (drawerItemsSource && drawerItemsTarget) {
        drawerItemsTarget.innerHTML = drawerItemsSource.innerHTML;
        if (drawerItemsTarget._restoreStickyErrors) drawerItemsTarget._restoreStickyErrors();
        if (typeof window.loadDrawerRatings === 'function') window.loadDrawerRatings();
      }

      // Update checkout section (total, button) but not recommendations
      var checkoutSource = drawerParsed.querySelector('.leh-cd-checkout');
      var checkoutTarget = document.querySelector('.leh-cd-checkout');
      if (checkoutSource && checkoutTarget) {
        checkoutTarget.innerHTML = checkoutSource.innerHTML;
      }
    }

    /* Cart page items */
    var cartItemsEl = document.getElementById('main-cart-items');
    if (cartItemsEl) {
      var cartItemsSection = cartItemsEl.dataset.id;
      var html = data.sections[cartItemsSection];
      if (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.querySelector('.js-contents');
        var target = cartItemsEl.querySelector('.js-contents');
        if (source && target) target.innerHTML = source.innerHTML;
      }
      var cartItemsComponent = document.querySelector('cart-items');
      if (cartItemsComponent && cartItemsComponent._restoreStickyErrors) cartItemsComponent._restoreStickyErrors();
    }

    /* Cart footer / order summary */
    var footer = document.getElementById('main-cart-footer');
    if (footer) {
      var footerSection = footer.dataset.id;
      var footerHtml = data.sections[footerSection];
      if (footerHtml) {
        var footerParsed = new DOMParser().parseFromString(footerHtml, 'text/html');
        var footerSource = footerParsed.querySelector('.js-contents');
        var footerTarget = footer.querySelector('.js-contents');
        if (footerSource && footerTarget) footerTarget.innerHTML = footerSource.innerHTML;
      }
    }

    /* Cart icon bubble */
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data.sections['cart-icon-bubble']) {
      var bubbleParsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
      var bubbleSource = bubbleParsed.querySelector('.shopify-section');
      if (bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
    }

    /* Toggle empty state */
    var isEmpty = data.item_count === 0;
    var cartEl = document.querySelector('cart-items');
    if (cartEl) cartEl.classList.toggle('is-empty', isEmpty);
    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    if (footer) footer.classList.toggle('is-empty', isEmpty);
    var drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
  }

  /**
   * Add item(s) to cart
   * @param {number|string} variantId
   * @param {number} quantity
   * @param {object} [properties] - line item properties e.g. { "House Number": "1234" }
   * @returns {Promise<object>} cart response data
   */
  function add(variantId, quantity, properties) {
    return enqueue(function () {
      var sections = getSections();
      var fd = new FormData();
      fd.append('id', String(variantId));
      fd.append('quantity', String(quantity || 1));
      fd.append('sections', sections.join(','));
      fd.append('sections_url', window.location.pathname);
      if (properties) {
        Object.keys(properties).forEach(function (key) {
          fd.append('properties[' + key + ']', properties[key]);
        });
      }

      return fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: fd,
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) throw data;
          renderSections(data);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-handler',
            cartData: data,
            variantId: variantId,
          }).catch(function () {});
          // Open the cart drawer after adding (skip if already open to preserve scroll position)
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) drawer.open();
          if (typeof window.loadDrawerRecs === 'function') window.loadDrawerRecs();
          return data;
        });
    });
  }

  /**
   * Change line item quantity
   * @param {number|string} lineOrId - 1-based line index, or a stable line-item key
   *   (e.g. "40000000:abc..."). Keys are preferred: they survive cart re-indexing,
   *   so rapid removals never target the wrong/non-existent line.
   * @param {number} quantity
   * @param {object} [options] - { render: true } set render:false to skip UI update
   * @returns {Promise<object>} cart response data
   */
  function change(lineOrId, quantity, options) {
    var shouldRender = !options || options.render !== false;
    return enqueue(function () {
      var sections = getSections();
      var payload = {
        quantity: quantity,
        sections: sections,
        sections_url: window.location.pathname,
      };
      // A line-item key always contains a ':' separator; a positional index never does.
      if (typeof lineOrId === 'string' && lineOrId.indexOf(':') !== -1) {
        payload.id = lineOrId;
      } else {
        payload.line = lineOrId;
      }
      var body = JSON.stringify(payload);

      return fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body,
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (shouldRender) renderSections(data);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-handler',
            cartData: data,
          }).catch(function () {});
          return data;
        });
    });
  }

  /**
   * Update cart attributes/note
   * @param {object} updates - { note, attributes, updates, etc. }
   * @returns {Promise<object>} cart response data
   */
  function update(updates) {
    return enqueue(function () {
      var sections = getSections();
      var payload = Object.assign({}, updates, {
        sections: sections,
        sections_url: window.location.pathname,
      });

      return fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          renderSections(data);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-handler',
            cartData: data,
          }).catch(function () {});
          return data;
        });
    });
  }

  return { add: add, change: change, update: update };
})();
